import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  PackageCheck,
  Globe2,
  Target,
  TrendingUp,
  BarChart3,
  Route as RouteIcon,
  Sparkles,
} from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  tooltipStyle,
  CHART_COLORS,
  fmt,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { hsOpportunities, perspectiveSnapshot } from "@/lib/analytics";
import { perspectiveConfig } from "@/lib/perspective";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Overview — Ready4Trade" },
      {
        name: "description",
        content:
          "Buyer-lead intelligence with HS 2022 and pooled RCA analysis for Uzbekistan and Slovakia.",
      },
    ],
  }),
  component: Overview,
});

function Overview() {
  const { leads, aggregates, isLoading, error, perspective } = useTradeData();
  const config = perspectiveConfig(perspective);
  const snapshot = useMemo(
    () => (leads ? perspectiveSnapshot(leads, perspective) : null),
    [leads, perspective],
  );
  const hsRows = useMemo(
    () =>
      leads
        ? hsOpportunities(leads, perspective)
            .filter((row) => (row.rca ?? 0) > 1)
            .slice(0, 12)
        : [],
    [leads, perspective],
  );

  return (
    <DashboardShell meta={aggregates?.meta}>
      {isLoading && <LoadingPanel />}
      {error && (
        <div className="surface-panel p-5 border-destructive/30 text-sm text-destructive">
          {error}
        </div>
      )}
      {aggregates && snapshot && leads && (
        <>
          <PageHeading
            title={`${config.flag} Export opportunity overview · ${config.country}`}
            subtitle={`Buyer demand is compared with ${config.country}'s HS6 revealed comparative advantage. The main RCA measure pools CEPII BACI trade values for 2022–2024.`}
            badge={`${fmt(snapshot.aligned)} RCA-aligned leads`}
          />

          <KpiRow snapshot={snapshot} total={aggregates.meta.total} />

          <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <ChartCard
              className="xl:col-span-2"
              title="RCA-aligned demand by sector"
              subtitle={`All buyer leads versus leads where ${config.country}'s pooled RCA is above 1`}
              icon={<Sparkles className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={390}>
                <BarChart data={snapshot.sectors} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={185}
                    tick={{ fontSize: 11, fill: "#334155" }}
                    stroke="#cbd5e1"
                  />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
                  <Bar dataKey="count" name="All leads" fill="#cbd5e1" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="aligned" name="RCA > 1" fill="#10b981" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Perspective-specific priority"
              subtitle="Score combines pooled RCA, supplier scope, distance, recency and commercial completeness"
              icon={<Target className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={390}>
                <PieChart>
                  <Pie
                    data={snapshot.scoreBands}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="43%"
                    innerRadius={68}
                    outerRadius={112}
                    paddingAngle={2}
                  >
                    {snapshot.scoreBands.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <ChartCard
              className="xl:col-span-2"
              title="Top HS6 opportunities"
              subtitle="High-demand products where pooled RCA is above 1"
              icon={<BarChart3 className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={430}>
                <BarChart data={hsRows} layout="vertical" margin={{ left: 10, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <YAxis
                    dataKey={(row: (typeof hsRows)[number]) =>
                      `${row.code} · ${trim(row.description, 34)}`
                    }
                    type="category"
                    width={250}
                    tick={{ fontSize: 10, fill: "#334155" }}
                    stroke="#cbd5e1"
                  />
                  <Tooltip
                    cursor={{ fill: "#f1f5f9" }}
                    contentStyle={tooltipStyle}
                    formatter={(value, name) => [value, name === "aligned" ? "Buyer leads" : name]}
                  />
                  <Bar dataKey="aligned" name="Buyer leads" fill="#2563eb" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="RCA-aligned destination markets"
              subtitle={`Top delivery countries for products with ${config.country} RCA > 1`}
              icon={<Globe2 className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={430}>
                <BarChart
                  data={snapshot.alignedMarkets.slice(0, 12)}
                  layout="vertical"
                  margin={{ left: 8, right: 18 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={125}
                    tick={{ fontSize: 10, fill: "#334155" }}
                    stroke="#cbd5e1"
                  />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#06b6d4" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title="Buyer-lead volume over time"
              subtitle="Monthly demand signals in the active dataset"
              icon={<TrendingUp className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={snapshot.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    stroke="#cbd5e1"
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title={`Distance from ${config.origins}`}
              subtitle="Nearest configured origin for each destination; straight-line screening distance"
              icon={<RouteIcon className="w-4 h-4" />}
            >
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={snapshot.distances}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    stroke="#cbd5e1"
                    angle={-18}
                    textAnchor="end"
                    height={68}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {snapshot.distances.map((_, index) => (
                      <Cell
                        key={index}
                        fill={index < 2 ? "#10b981" : index < 4 ? "#2563eb" : "#8b5cf6"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          <section className="surface-panel p-5 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <PackageCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-foreground">Methodology</div>
                <p className="mt-1">
                  HS codes are standardized to HS 2022. RCA is the pooled Balassa index calculated
                  from CEPII BACI HS22 trade values for 2022–2024. Buyer leads are demand signals,
                  not confirmed orders. Distance is a geographic screening proxy, not a route,
                  transit-time or freight quotation.
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </DashboardShell>
  );
}

function KpiRow({
  snapshot,
  total,
}: {
  snapshot: ReturnType<typeof perspectiveSnapshot>;
  total: number;
}) {
  const items = [
    {
      label: "Buyer leads",
      value: fmt(total),
      icon: TrendingUp,
      sub: "Demand signals in active dataset",
      tone: "bg-blue-50 text-blue-600",
    },
    {
      label: "RCA-aligned leads",
      value: fmt(snapshot.aligned),
      icon: PackageCheck,
      sub: `${snapshot.withRca ? ((snapshot.aligned / snapshot.withRca) * 100).toFixed(1) : "0.0"}% of leads with RCA`,
      tone: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "High-priority leads",
      value: fmt(snapshot.highPriority),
      icon: Target,
      sub: "Perspective-specific score ≥ 70",
      tone: "bg-violet-50 text-violet-600",
    },
    {
      label: "Supplier-scope fit",
      value: fmt(snapshot.supplierOpen),
      icon: Globe2,
      sub: "Explicit, regional or worldwide",
      tone: "bg-cyan-50 text-cyan-600",
    },
    {
      label: "Average distance",
      value: `${fmt(snapshot.avgDistance)} km`,
      icon: RouteIcon,
      sub: "Nearest configured origin",
      tone: "bg-amber-50 text-amber-600",
    },
  ];
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
      {items.map((item) => (
        <div key={item.label} className="surface-panel p-4 flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${item.tone}`}
          >
            <item.icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">{item.label}</div>
            <div className="kpi-value mt-1">{item.value}</div>
            <div className="text-[11px] text-muted-foreground mt-1 truncate">{item.sub}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

function trim(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}
